import { Fn, float, If, smoothstep, struct, vec3, vec4 } from 'three/tsl';
import {
  type BakedNoise,
  bakeKey,
  type SurfaceContext,
  type TerrainStyle,
  type WaterStyle,
} from './TerrainStyle';

type N = any;

const Surface = struct({ albedo: 'vec3', roughness: 'float', emissive: 'vec3', detail: 'vec4' });
/** Weights at or below this skip their biome's palette (the atlas holds them in 1/255 steps). */
const WEIGHT_EPS = 0.002;

/** Channel `i` of a vec4 of biome weights. */
export function biomeWeight(w: N, i: number): N {
  return [w.x, w.y, w.z, w.w][i];
}

/** Detail materials for a palette that doesn't say: ground, and rock where it's steep. */
export function defaultDetail(slope: N): N {
  const rock = smoothstep(0.2, 0.38, slope);
  return vec4(float(1).sub(rock), rock, 0, 0);
}

/**
 * The ground of a world of several biomes: each biome's palette where its weight (ctx.biome,
 * see BiomeField) isn't zero, blended by weight. Every palette runs in a real branch, so away
 * from the borders a pixel pays for one palette, not all of them. Each palette still sees its
 * own forests (`this` is its own style). The water is the world's.
 */
export function mixStyles(styles: TerrainStyle[], water: WaterStyle): TerrainStyle {
  if (styles.length === 1) return { ...styles[0], water };
  return {
    bakedNoise: sharedBakes(styles),
    forestMask(ctx) {
      if (!ctx.biome) return styles[0].forestMask(ctx);
      let m: N = float(0);
      styles.forEach((s, i) => {
        m = m.add(s.forestMask(ctx).mul(biomeWeight(ctx.biome, i)));
      });
      return m;
    },
    land(ctx) {
      const w = ctx.biome;
      if (!w) return styles[0].land(ctx);
      const out = Fn(() => {
        // The inputs every palette reads, evaluated before the branches: a node first used in
        // one branch would be computed only there, and read as 0 in the others.
        const c: SurfaceContext = {
          ...ctx,
          p: vec3(ctx.p).toVar(),
          h: float(ctx.h).toVar(),
          slope: float(ctx.slope).toVar(),
          dist: float(ctx.dist).toVar(),
          near: float(ctx.near).toVar(),
          mid: float(ctx.mid).toVar(),
          footprint: ctx.footprint === undefined ? undefined : float(ctx.footprint).toVar(),
          biome: vec4(w).toVar(),
          ridge: ctx.ridge === undefined ? undefined : vec3(ctx.ridge).toVar(),
          baked: ctx.baked ? new Map([...ctx.baked].map(([k, v]) => [k, float(v).toVar()])) : undefined,
        };
        const albedo = vec3(0).toVar();
        const roughness = float(0).toVar();
        const emissive = vec3(0).toVar();
        const detail = vec4(0).toVar();
        styles.forEach((s, i) => {
          const wi = biomeWeight(c.biome, i);
          If(wi.greaterThan(WEIGHT_EPS), () => {
            const l = s.land(c);
            albedo.addAssign(l.albedo.mul(wi));
            roughness.addAssign(float(l.roughness).mul(wi));
            if (l.emissive) emissive.addAssign(l.emissive.mul(wi));
            detail.addAssign(vec4(l.detail ?? defaultDetail(c.slope)).mul(wi));
          });
        });
        return Surface(albedo, roughness, emissive, detail);
      })();
      return {
        albedo: out.get('albedo'),
        roughness: out.get('roughness'),
        emissive: out.get('emissive'),
        detail: out.get('detail'),
      };
    },
    water,
  };
}

/**
 * The broad noise terms the tiles carry for a world of several biomes: all of their palettes'
 * (TerrainStyle.bakedNoise), the ones most of them use first, up to the generator's 8.
 */
function sharedBakes(styles: TerrainStyle[]): BakedNoise[] {
  const uses = new Map<string, { term: BakedNoise; count: number; first: number }>();
  let order = 0;
  for (const s of styles) {
    for (const term of s.bakedNoise ?? []) {
      const k = bakeKey(term.wavelength, term.offset);
      const u = uses.get(k);
      if (u) u.count++;
      else uses.set(k, { term, count: 1, first: order++ });
    }
  }
  return [...uses.values()]
    .sort((a, b) => b.count - a.count || a.first - b.first)
    .slice(0, 8)
    .map((u) => u.term);
}
